import {
  Dataset,
  DatasetExportData,
  DuplicatesThresholdRange,
  EnrichmentModel,
  EntityType,
  ExportData,
  ExportType,
  FilterItemCounts,
  FilterItemCountsWithSelect,
  FilterMenuItem,
  FilterOption,
  Filters,
  FiltersObject,
  isValidEntityType,
  MetadataItem,
  MetadataSource,
  MetadataSummaryData,
  MetadataType,
  QueryModalData,
  RawMetadataSummaryData,
  SearchFilterOptions,
  SingleExportItem,
  VqlQuery,
} from "types";
import { createSelector } from "@reduxjs/toolkit";
import * as Features from "helpers/constants/filters";
import { TRAIN_MODEL } from "helpers/constants/filters";
import { isFeatureEnabled } from "helpers/utility/utilities";
import { initialEntityTypeFilter } from "./reducer";
import {
  buildAPIQueryParams,
  getEntityTypeValue as getEntityTypeValueUtil,
  getSimilarityThresholdValue,
} from "helpers/utility/queryUtils";
import {
  DUPLICATE_THRESHOLD,
  ENTITY_TYPE_PARAMETER,
  FOLDER_PARAMETER,
  ISSUE_CONFIDENCE,
  ISSUE_TYPE_PARAMETER,
  LABELS_PARAMETER,
  METADATA_SOURCES_PARAMETER,
  SEARCH_TEXT_PARAMETER,
  USER_TAGS_PARAMETER,
  VERTEX_CONTEXT,
  VERTEX_TYPE,
  VQL_PARAMETER,
} from "helpers/constants/miscellaneous";

import { State } from "redux/store";
import { getIsAddMediaEnabled } from "../Datasets/selectors";

const state = (state: any) => state.singleDataset;

const sortTags = (tags: MetadataItem[]) => {
  return tags.sort((a, b) => {
    if (a.text === "untagged") {
      return -1;
    } else if (b.text === "untagged") {
      return 1;
    } else {
      return a.text.localeCompare(b.text);
    }
  });
};

function createContextualFilterOptions(
  contextualItems: FilterOption[],
  allItems: FilterOption[],
  selectedItems: (string | number)[],
  selectedBy: (item: FilterOption) => string | number,
  sortFn: (items: FilterOption[]) => FilterOption[],
): FilterOption[] {
  const contextualWithSelection: FilterOption[] = contextualItems.map(
    (item) => ({
      ...item,
      selected: selectedItems.includes(selectedBy(item)),
    }),
  );

  const missingSelected = selectedItems.filter(
    (sel) => !contextualItems.some((item) => selectedBy(item) === sel),
  );
  let missingWithSelection: FilterOption[] = [];
  missingSelected.forEach((sel) => {
    const fullItem = allItems.find((item) => selectedBy(item) === sel);
    if (!fullItem) {
      return;
    }

    // handle duplicate ids caused by the same label in selection and newLabels
    const existingId = contextualWithSelection.find(
      (item) => item.id === fullItem.id,
    );
    const uniqueId = existingId ? fullItem.id + 0.99 : fullItem.id;

    missingWithSelection.push({
      id: uniqueId,
      text: fullItem.text,
      selected: true,
    });
  });
  return sortFn([...contextualWithSelection, ...missingWithSelection]);
}
export const getContentLoading = createSelector(state, (state: any) => {
  return state.isContentLoading;
});

export const getIsCardslistLoading = createSelector(state, (state: any) => {
  return state.isCardslistLoading;
});

export const getIsLabelLoading = createSelector(state, (state: any) => {
  return state.isLabelLoading;
});

export const getIsIssuesLoading = createSelector(state, (state: any) => {
  return state.isIssueTypeLoading;
});

export const getIsFileFilterLoading = createSelector(state, (state: any) => {
  return state.isFileFilterLoading;
});
/**
 * @deprecated this function is deprecated use useUserConfig hook instead
 */
export const getIsUserConfigLoading = createSelector(state, (state: any) => {
  return state.isUserConfigLoading;
});

export const getIssueId = createSelector(state, (state: any) => {
  return state.issueId;
});

export const getSeverityId = createSelector(state, (state: any) => {
  return state.severityId;
});

export const getIssueData = createSelector(state, (state: any) => {
  return state.issues;
});

export const getSeverityData = createSelector(state, (state: any) => {
  return state.stats;
});

export const getDatasetSortingValue = createSelector(state, (state: any) => {
  return state.datasetSortingValue;
});

const createTagsSelector = (stateSelector: any, stateKey: string) => {
  const baseSelector = createSelector(
    stateSelector,
    (state: any): MetadataItem[] => {
      return state[stateKey];
    },
  );

  const sortedSelector = createSelector(
    [baseSelector, (state: any, selectedTags: number[]) => selectedTags],
    (tags: MetadataItem[], selectedTags: number[]): FilterOption[] => {
      let sortedTags = [...tags];
      sortedTags = sortedTags.sort((a: MetadataItem, b: MetadataItem) => {
        if (a.text === "untagged") {
          return -1;
        } else if (b.text === "untagged") {
          return 1;
        } else {
          return a.text.localeCompare(b.text);
        }
      });

      return sortedTags.map((tag: MetadataItem) => ({
        ...tag,
        selected: selectedTags.includes(tag.id),
      }));
    },
  );

  return {
    base: baseSelector,
    sorted: sortedSelector,
  };
};

export const getContextualUserTags = createSelector(
  [state, (state: any, selectedTagIds: number[]) => selectedTagIds],
  (state: any, selectedTagIds: number[]) =>
    createContextualFilterOptions(
      state.contextualUserTags,
      state.userTags,
      selectedTagIds,
      (item) => item.id,
      sortTags,
    ),
);

const userTagsSelectors = createTagsSelector(state, "userTags");

export const getTags = userTagsSelectors.base;
export const getUserTags = userTagsSelectors.sorted;

export const getIssueTypes = createSelector(state, (state): MetadataItem[] => {
  return state.issueTypes;
});

//Query Filter States
export const getSelectedQueryModal = createSelector(
  state,
  (state): QueryModalData | null => {
    return state.selectedQueryModal;
  },
);

export const getSimilarityVertexCluster = createSelector(
  state,
  (state: any) => {
    return state.similarityVertexCluster;
  },
);

export const getTreeFolderRaw = createSelector(state, (state: any) => {
  return state.treeFolder;
});

const recursivelyUpdateTreeFolder = (treeFolder: any) => {
  let processedTreeFolder = Object.create(null);
  processedTreeFolder.key = treeFolder.full_path;
  processedTreeFolder.title = treeFolder.name;
  if (treeFolder.children) {
    processedTreeFolder.children = treeFolder.children.map((child: any) =>
      recursivelyUpdateTreeFolder(child),
    );
  } else {
    processedTreeFolder.isLeaf = true;
  }
  return processedTreeFolder;
};

export const getTreeFolder = createSelector(
  getTreeFolderRaw,
  (treeFolder: any) => {
    return treeFolder
      ? recursivelyUpdateTreeFolder(treeFolder)?.children
      : null;
  },
);

export const getFoldersFromTreeFolder = createSelector(
  getTreeFolderRaw,
  (treeFolder: any) => {
    if (!treeFolder) return [];

    const folders: {
      id: number;
      full_path: string;
      name: string;
      selected: boolean;
    }[] = [];
    let fileId = 1;

    const traverseTree = (node: any) => {
      if (node.type === "FOLDER") {
        folders.push({
          id: fileId++,
          full_path: node.full_path,
          name: node.name,
          selected: false,
        });
      }

      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child: any) => traverseTree(child));
      }
    };

    traverseTree(treeFolder);
    return folders;
  },
);

export const getFilesFromTreeFolder = createSelector(
  getTreeFolderRaw,
  (treeFolder: any) => {
    if (!treeFolder) return [];

    const files: {
      id: number;
      full_path: string;
      name: string;
      selected: boolean;
    }[] = [];
    let fileId = 1;

    const traverseTree = (node: any) => {
      if (!["FOLDER", "ROOT"].includes(node.type)) {
        files.push({
          id: fileId++,
          full_path: node.full_path,
          name: node.name,
          selected: false,
        });
      }

      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child: any) => traverseTree(child));
      }
    };

    traverseTree(treeFolder);
    return files;
  },
);

export const getSimilarityThreshold = createSelector(
  [
    state,
    (state: any, selectedSimilarityThreshold: string | undefined) =>
      selectedSimilarityThreshold,
  ],
  (state: any, selectedSimilarityThreshold: string | undefined) => {
    return getSimilarityThresholdValue(state, selectedSimilarityThreshold);
  },
);

export const getSimilarityClusters = createSelector(state, (state: any) => {
  return state.similarityClusters;
});

export const getEntityTypes = createSelector(state, (state: any) => {
  return state.entityTypeFilter.sort((a: MetadataItem, b: MetadataItem) =>
    a.text.localeCompare(b.text),
  );
});

export const getEntityTypeFilter = createSelector(
  [
    getEntityTypes,
    (state: any, entityTypeFromQueryParam: EntityType | null) =>
      entityTypeFromQueryParam,
  ],
  (
    entityTypes: MetadataItem[],
    entityTypeFromQueryParam: EntityType | null,
  ): FilterOption[] => {
    return entityTypes.map((entity: MetadataItem) => ({
      ...entity,
      selected: entityTypeFromQueryParam
        ? entityTypeFromQueryParam === entity.text.toUpperCase()
        : false,
    }));
  },
);

export const getFilteredSimilarityClusters = createSelector(
  getSimilarityClusters,
  getSimilarityVertexCluster,
  (similarityClusters: any, similarityVertexCluster: any) => {
    if (similarityVertexCluster) {
      return similarityClusters.filter((cluster: any) => {
        if (cluster.flat_response) {
          // in flat mode, each cluster has 1 preview, and multiple previews can have the same cluster_id
          // so filter by the media_id
          return (
            cluster.previews[0].media_id !==
            similarityVertexCluster.previews[0].media_id
          );
        }
        return cluster.cluster_id !== similarityVertexCluster.cluster_id;
      });
    } else {
      return similarityClusters;
    }
  },
);

export const getStats = createSelector(
  state,
  (state): { dataset: Dataset } | null => {
    return state.stats;
  },
);

export const getDataset = createSelector(getStats, (stats) => {
  return stats?.dataset as any;
});

const EMPTY_THRESHOLDS = {
  minThreshold: 0,
  maxThreshold: 0,
  values: [],
  count: 0,
};
export const getThresholdsRange = createSelector(state, (state: any) => {
  const simThreasholdsArray = state.similarityThresholds;
  if (Array.isArray(simThreasholdsArray) && simThreasholdsArray.length > 0) {
    const simThresholdsNum = simThreasholdsArray
      .map((threshold: string) => parseFloat(threshold))
      .filter((t: number) => !isNaN(t));
    if (simThresholdsNum.length === 0) {
      return EMPTY_THRESHOLDS;
    }

    return {
      minThreshold: Math.min(...simThresholdsNum),
      maxThreshold: Math.max(...simThresholdsNum),
      values: simThreasholdsArray,
      count: simThreasholdsArray.length,
    };
  } else {
    // Return a default range when no thresholds are available
    return EMPTY_THRESHOLDS;
  }
});

export const getContextualLabels = createSelector(state, (state: any) => {
  return state.contextualLabels;
});

const getLabels = createSelector(state, (state: any): MetadataItem[] => {
  return state.labels;
});

const getSortedLabels = (labels: FilterOption[]) => {
  return labels.sort((a, b) => {
    if (a.text === "unlabeled") {
      return -1;
    } else if (b.text === "unlabeled") {
      return 1;
    } else {
      return a.text.localeCompare(b.text);
    }
  });
};

export const getDatasetLabels = createSelector(
  [getLabels, (state: any, selectedLabels: string[]) => selectedLabels],
  (labels: MetadataItem[], selectedLabels: string[]): FilterOption[] => {
    const labelsWithSelect = labels.map((label) => ({
      ...label,
      selected: selectedLabels.includes(label.text),
    }));

    return getSortedLabels(labelsWithSelect);
  },
);

export const getDatasetContextualLabels = createSelector(
  [
    getContextualLabels,
    getLabels,
    (state: any, selectedLabels: string[]) => selectedLabels,
  ],
  (contextualLabels, labels, selectedLabels) => {
    return createContextualFilterOptions(
      contextualLabels,
      labels,
      selectedLabels,
      (item) => item.text,
      getSortedLabels,
    );
  },
);

const getSortedIssueTypes = (
  issueTypes: any[],
  sectionType: "TopFilter" | "RightSection",
) => {
  const PRIORITY_ISSUES = ["duplicates", "mislabels", "outliers"];

  let key = "title";
  if (sectionType === "TopFilter") {
    key = "text";
  } else if (sectionType === "RightSection") {
    key = "title";
  }

  // Split the items into priority and non-priority items
  const priorityItems = issueTypes
    .filter((item) => PRIORITY_ISSUES.includes(item[key].toLowerCase()))
    .sort(
      (a, b) =>
        PRIORITY_ISSUES.indexOf(a[key].toLowerCase()) -
        PRIORITY_ISSUES.indexOf(b[key].toLowerCase()),
    );

  // Sort the non-priority items
  const nonPriorityItems = issueTypes
    .filter((item) => !PRIORITY_ISSUES.includes(item[key].toLowerCase()))
    .sort((a, b) => {
      if (sectionType === "RightSection") {
        return b.count - a.count;
      } else if (sectionType === "TopFilter") {
        return a.text.localeCompare(b.text);
      } else return 0;
    });

  // Concatenate priority items (in their original order) with non-priority items
  return [...priorityItems, ...nonPriorityItems];
};

export const getDatasetIssueTypes = createSelector(
  [
    getIssueTypes,
    (state: any, selectedIssueTypes: number[]) => selectedIssueTypes,
  ],
  (
    issueTypes: MetadataItem[],
    selectedIssueTypes: number[],
  ): FilterOption[] => {
    let sortedIssueTypes = [...issueTypes];
    sortedIssueTypes = getSortedIssueTypes(sortedIssueTypes, "TopFilter");

    return sortedIssueTypes.map((issueType: MetadataItem) => ({
      ...issueType,
      selected: selectedIssueTypes.includes(issueType.id),
    }));
  },
);

export const getNavigationCluster = createSelector(state, (state: any) => {
  return state.navigationCluster;
});

export const getFilteredNavigationCluster = createSelector(
  getNavigationCluster,
  getSimilarityVertexCluster,
  (navigationCluster: any, similarityVertexCluster: any) => {
    if (
      similarityVertexCluster &&
      similarityVertexCluster.previews &&
      similarityVertexCluster.previews.length === 1 &&
      navigationCluster &&
      navigationCluster.previews
    ) {
      return {
        ...navigationCluster,
        previews: navigationCluster.previews.filter(
          (preview: any) =>
            preview.media_id !== similarityVertexCluster.previews[0].media_id,
        ),
      };
    } else {
      return navigationCluster;
    }
  },
);

const getNavigationClusterTags = createSelector(state, (state: any) => {
  return state.navigationClusterTags;
});

export const getFilteredNavigationClusterTags = createSelector(
  getNavigationClusterTags,
  getSimilarityVertexCluster,
  (navigationClusterTags: any, similarityVertexCluster: any) => {
    if (
      similarityVertexCluster &&
      similarityVertexCluster.previews &&
      similarityVertexCluster.previews.length === 1 &&
      navigationClusterTags &&
      navigationClusterTags.previews
    ) {
      return {
        ...navigationClusterTags,
        previews: navigationClusterTags.previews.filter(
          (preview: any) =>
            preview.media_id !== similarityVertexCluster.previews[0].media_id,
        ),
      };
    } else {
      return navigationClusterTags;
    }
  },
);

export const getNavigationClusterLoader = createSelector(
  state,
  (state: any) => {
    return state.navigationClusterLoader;
  },
);

export const getFileNameSearchText = createSelector(state, (state: any) => {
  return state.fileNameSearchText;
});

export const getUserConfig = createSelector(state, (state: any) => {
  return state.filterConfig;
});

export const getRawExportData = createSelector(
  state,
  (state): ExportData | null => {
    return state.exportData;
  },
);

export const getDatasetExportData = createSelector(
  [getStats, getRawExportData],
  (stats, exportData): DatasetExportData | null => {
    const datasetID = stats?.dataset?.id || "";
    return exportData?.[datasetID] || null;
  },
);

export const getExportDataArray = createSelector(
  getDatasetExportData,
  (exportData): SingleExportItem[] => {
    return exportData?.exportDataArray || [];
  },
);

export const getExportCount = createSelector(
  getExportDataArray,
  (exportDataArray) => {
    return exportDataArray.length;
  },
);

export const getExportTags = createSelector(
  getDatasetExportData,
  (exportData): any[] => {
    return exportData?.tags || [];
  },
);

export const getTemporaryExportData = createSelector(
  state,
  (state): DatasetExportData | null => {
    return state.temporaryExportData;
  },
);

export const getTemporaryExportDataArray = createSelector(
  getTemporaryExportData,
  (exportData): SingleExportItem[] => {
    return exportData?.exportDataArray || [];
  },
);

export const getTempExportCount = createSelector(
  getTemporaryExportDataArray,
  (exportDataArray) => {
    return exportDataArray.length;
  },
);

export const getShowCart = createSelector(state, (state: any) => {
  return state.showCart;
});

export const getTempExportData = createSelector(state, (state: any) => {
  return state.tempExportData;
});

export const getExportDataTimeoutID = createSelector(state, (state: any) => {
  return state.exportDataTimeoutID;
});

export const getIsUserConfigLoaded = createSelector(
  getUserConfig,
  (userConfig: any) => userConfig?.length > 0,
);

export const isSharingEnabled = createSelector(
  getUserConfig,
  (userConfig: any) => {
    return isFeatureEnabled(userConfig, Features.DATASET_SHARE);
  },
);

export const getEntityTypeValue = createSelector(
  getEntityTypeFilter,
  (entityTypeFilter: FilterOption[]): EntityType => {
    let result: EntityType = "IMAGES";
    const entityTypeValues = [...entityTypeFilter];
    if (entityTypeValues) {
      let noSelection = entityTypeValues.every(
        (value: FilterOption) => value.selected === false,
      );

      if (noSelection) {
        result = entityTypeValues[0].text.toUpperCase() as EntityType;
      } else {
        let selectedEntity = entityTypeValues.find(
          (value: FilterOption) => value.selected === true,
        );
        result = selectedEntity?.text.toUpperCase() as EntityType | "IMAGES";
      }
    }
    return result;
  },
);

export const getIsSearchByRelevanceEnabled = createSelector(
  getUserConfig,
  getEntityTypeValue,
  (userConfig: any, entityTypeValues: EntityType): boolean => {
    const featureKeyMap: Record<EntityType, string> = {
      IMAGES: Features.TEXTUAL_SEARCH_RELEVANCE_SCORE_IMAGES,
      OBJECTS: Features.TEXTUAL_SEARCH_RELEVANCE_SCORE_OBJECTS,
    };

    return isFeatureEnabled(userConfig, featureKeyMap[entityTypeValues]);
  },
);

export const getSearchOptions = createSelector(
  getUserConfig,
  getEntityTypeValue,
  (userConfig: any, entityType: EntityType): SearchFilterOptions => {
    const searchOptions: SearchFilterOptions = {
      labelsSearch: false,
      semanticSearch: false,
      enabled: false,
    };

    const featureKeyMap: Record<EntityType, string> = {
      IMAGES: Features.TEXTUAL_SEARCH_IMAGE,
      OBJECTS: Features.TEXTUAL_SEARCH_OBJECT,
    };
    const featureKey = featureKeyMap[entityType];

    if (featureKey) {
      const feature = userConfig.find(
        (feature: any) => feature.feature_key === featureKey,
      );
      if (feature) {
        searchOptions.labelsSearch =
          feature.featureOptions?.labels_search || searchOptions.labelsSearch;

        searchOptions.semanticSearch =
          feature.featureOptions?.semantic_search ||
          searchOptions.semanticSearch;

        searchOptions.enabled = feature.feature_behavior["ANY"] === "SHOW";
      }
    }

    return searchOptions;
  },
);

export const getIsSearchEnabled = createSelector(
  getSearchOptions,
  (searchOptions): boolean => searchOptions.enabled,
);

export const isImageSearchEnabled = createSelector(
  getUserConfig,
  (userConfig: any) => {
    return isFeatureEnabled(userConfig, Features.VISUAL_SEARCH);
  },
);

export const getIsRecentSearchEnabled = createSelector(
  getUserConfig,
  (userConfig: any) => {
    return isFeatureEnabled(userConfig, Features.AUTO_SUGGEST_RECENT_SEARCH);
  },
);

export const getIsAutoSuggestQueryEnabled = createSelector(
  getUserConfig,
  (userConfig: any) => {
    return isFeatureEnabled(userConfig, Features.AUTO_SUGGEST_QUERY);
  },
);

export const isFindSimilarObjectEnabled = createSelector(
  getUserConfig,
  (userConfig: any) => {
    return isFeatureEnabled(userConfig, Features.FIND_SIMILAR_OBJECT_FILTER);
  },
);

export const isRegionSearchEnabled = createSelector(
  getUserConfig,
  (userConfig: any) => {
    return isFeatureEnabled(userConfig, Features.REGION_SELECTION_SIMILARITY);
  },
);

export const getScrollApplied = createSelector(state, (state: any) => {
  return state.scrollApplied;
});

export const getDataPageScrollPosition = createSelector(state, (state: any) => {
  return state.dataPageScrollPosition;
});

export const getClusterPageScrollPosition = createSelector(
  state,
  (state: any) => {
    return state.clusterPageScrollPosition;
  },
);

export const getPaginationMetadataForData = createSelector(
  state,
  (state: any) => {
    return state.paginationMetadataForData;
  },
);

export const getTotalPagesForData = createSelector(
  getPaginationMetadataForData,
  (metadata?: { total: number; size: number }) => {
    if (metadata) {
      const { total, size } = metadata;
      return total < size ? 1 : Math.ceil(total / size);
    } else {
      return 0;
    }
  },
);

export const getPaginationMetadataForCluster = createSelector(
  state,
  (state: any) => {
    return state.paginationMetadataForCluster;
  },
);

export const getTotalPagesForCluster = createSelector(
  getPaginationMetadataForCluster,
  (metadata?: { total: number; size: number }) => {
    if (metadata) {
      const { total, size } = metadata;
      return total < size ? 1 : Math.ceil(total / size);
    } else {
      return 0;
    }
  },
);

const getMDQueryParams = (_: any, queryParams: FiltersObject, __: any) => {
  return queryParams;
};

export const getRawMetadataSummary = createSelector(
  [state, (_: any, __: any, isDataPage: boolean) => isDataPage],
  (state: any, isDataPage: boolean): RawMetadataSummaryData => {
    return isDataPage
      ? state.dataMetadataSummary
      : state.clusterMetadataSummary;
  },
);

export const getMetadataSummaryLoading = createSelector(
  [state, (_: any, __: any, isDataPage: boolean) => isDataPage],
  (state: any, isDataPage: boolean): boolean => {
    return isDataPage
      ? state.dataMetadataSummaryLoading
      : state.clusterMetadataSummaryLoading;
  },
);

export const getVideoDataWithSelect = createSelector(
  [getRawMetadataSummary, getMDQueryParams],
  (rawMd, queryParams): FilterItemCountsWithSelect[] => {
    if (!rawMd) return [];

    const selectedFolder = queryParams[FOLDER_PARAMETER] || "";
    return rawMd.videoData.map((item: FilterItemCounts) => ({
      ...item,
      selected: selectedFolder === item.title,
    }));
  },
);

export const getUserTagsWithSelect = createSelector(
  [getTags, getRawMetadataSummary, getMDQueryParams],
  (userTags, rawMd, queryParams): FilterItemCountsWithSelect[] => {
    if (!rawMd) return [];

    const selectedIds = new Set(
      JSON.parse(queryParams[USER_TAGS_PARAMETER] || "[]"),
    );

    return rawMd.userTags.map((item: FilterItemCounts) => ({
      ...item,
      selected: userTags.some(
        (tag: FilterOption) =>
          tag.text === item.title && selectedIds.has(tag.id.toString()),
      ),
    }));
  },
);

export const getMediaLabelsWithSelect = createSelector(
  [getRawMetadataSummary, getMDQueryParams],
  (rawMd: any, queryParams): FilterItemCountsWithSelect[] => {
    if (!rawMd) return [];

    const selectedLabels = new Set(
      JSON.parse(queryParams[LABELS_PARAMETER] || "[]"),
    );
    const result: FilterItemCountsWithSelect[] = [];

    rawMd.mediaLabels.forEach((item: FilterItemCounts) => {
      const selected = selectedLabels.has(item.title);
      if (item.title === "unlabeled") {
        result.unshift({ ...item, selected });
      } else {
        result.push({ ...item, selected });
      }
    });

    return result;
  },
);

const getIssueTypeNames = (title: string) => {
  switch (title) {
    case "label outlier":
      return "class outliers";

    case "blur":
      return "blurry";

    default:
      return title;
  }
};

export const getIssueTypesWithSelect = createSelector(
  [getRawMetadataSummary, getIssueTypes, getMDQueryParams],
  (rawMd: any, issueTypes, queryParams): FilterItemCountsWithSelect[] => {
    if (!rawMd) return [];

    const selectedIds = new Set(
      JSON.parse(queryParams[ISSUE_TYPE_PARAMETER] || "[]"),
    );

    const issueTypesWithSelect = rawMd.issueData.map(
      (item: FilterItemCounts) => ({
        ...item,
        title: getIssueTypeNames(item.title),
        selected: issueTypes.some(
          (issue: FilterOption) =>
            issue.text === item.title && selectedIds.has(issue.id),
        ),
      }),
    );

    return getSortedIssueTypes(issueTypesWithSelect, "RightSection");
  },
);

export const getMetadataSummary = createSelector(
  [
    getRawMetadataSummary,
    getVideoDataWithSelect,
    getUserTagsWithSelect,
    getIssueTypesWithSelect,
    getMediaLabelsWithSelect,
  ],
  (
    rawMd,
    videoData,
    userTags,
    issueTypes,
    mediaLabels,
  ): MetadataSummaryData => {
    if (!rawMd) {
      return {
        showVLEnrichment: false,
        videoData: [],
        userTags: [],
        issueTypes: [],
        mediaLabels: [],
      };
    }

    return {
      showVLEnrichment: rawMd.showVLEnrichment,
      videoData,
      userTags,
      issueTypes,
      mediaLabels,
    };
  },
);

export const getDatasetTags = createSelector(state, (state: any) => {
  return state.datasetTags;
});

export const getIsUserTagLoading = createSelector(state, (state: any) => {
  return state.isUserTagLoading;
});

export const getNavigationContext = createSelector(state, (state: any) => {
  return state.navigationContext;
});

export const getIsImageUploading = createSelector(state, (state: any) => {
  return state.isImageUploading;
});

export const getHoveredFileState = createSelector(state, (state: any) => {
  return state.hoveredFileState;
});

export const getFetchError = createSelector(state, (state: any) => {
  return state.fetchError;
});

export const getAutocompleteSuggestions = createSelector(
  state,
  (state: any) => {
    return state.autocompleteSuggestions;
  },
);

export const getRecentSearches = createSelector(state, (state: any) => {
  return state.recentSearches;
});

export const getAutocompleteLoading = createSelector(state, (state: any) => {
  return state.autocompleteLoading;
});

export const getAutocompleteNoResultsFlag = createSelector(
  state,
  (state: any) => {
    return state.autocompleteNoResultsFlag;
  },
);

export const getAllSelectedFilters = createSelector(
  (state: any, queryParams: FiltersObject) => queryParams,
  (queryParams) => {
    const selectedLabels = JSON.parse(queryParams[LABELS_PARAMETER] || "[]");
    const selectedUserTags = JSON.parse(
      queryParams[USER_TAGS_PARAMETER] || "[]",
    );
    const allSelectedFilters: { [key: string]: any } = {};
    if (selectedLabels.length > 0) {
      allSelectedFilters.labels = selectedLabels;
    }
    if (queryParams[ISSUE_TYPE_PARAMETER]) {
      allSelectedFilters.issues = JSON.parse(
        queryParams[ISSUE_TYPE_PARAMETER] || "[]",
      );
    }
    if (selectedUserTags.length > 0) {
      allSelectedFilters.tags = selectedUserTags;
    }
    // Use getEntityTypeValueUtil to handle fallback for unsupported entity types
    const entityTypeFromParam = isValidEntityType(
      queryParams[ENTITY_TYPE_PARAMETER],
    )
      ? queryParams[ENTITY_TYPE_PARAMETER]
      : null;
    allSelectedFilters.entity_type = getEntityTypeValueUtil(
      initialEntityTypeFilter,
      entityTypeFromParam,
    );
    if (queryParams[SEARCH_TEXT_PARAMETER]) {
      allSelectedFilters.caption = queryParams[SEARCH_TEXT_PARAMETER];
    }
    if (queryParams[FOLDER_PARAMETER]) {
      allSelectedFilters.path = decodeURIComponent(
        queryParams[FOLDER_PARAMETER] || "",
      );
    }
    if (queryParams[DUPLICATE_THRESHOLD]) {
      allSelectedFilters.duplicate_threshold = JSON.parse(
        queryParams[DUPLICATE_THRESHOLD],
      );
    }
    if (queryParams[ISSUE_CONFIDENCE]) {
      allSelectedFilters.issue_confidence = JSON.parse(
        queryParams[ISSUE_CONFIDENCE],
      );
    }
    if (queryParams[METADATA_SOURCES_PARAMETER]) {
      const metadataSources = queryParams[METADATA_SOURCES_PARAMETER];
      const source = metadataSources.includes("vl_flywheel_v00")
        ? ["vl_flywheel_v00"]
        : metadataSources.includes("user_labels_annotations_file")
          ? ["user_labels_annotations_file"]
          : null;
      if (source) {
        allSelectedFilters[METADATA_SOURCES_PARAMETER] = JSON.stringify(source);
      }
    }
    if (queryParams[VQL_PARAMETER]) {
      allSelectedFilters.vql = JSON.parse(queryParams[VQL_PARAMETER]);
    }
    return allSelectedFilters;
  },
);

export const getImagePageParamsContext = createSelector(state, (state: any) => {
  return state.imagePageParamsContext;
});

export const getCurrentFilterQueries = createSelector(
  [
    state,
    (state: any, queryParams: FiltersObject = {}) => queryParams,
    (
      state: any,
      queryParams: FiltersObject = {},
      sendPageParameter: boolean = true,
    ) => sendPageParameter,
    (
      state: any,
      queryParams: FiltersObject = {},
      sendPageParameter: boolean = true,
      sendVertexParams: boolean = true,
    ) => sendVertexParams,
  ],
  (
    state: any,
    queryParams: FiltersObject = {},
    sendPageParameter: boolean = true,
    sendVertexParams: boolean = true,
  ) => {
    const apiQueryParams = buildAPIQueryParams(state, queryParams);
    if (!sendPageParameter) {
      delete apiQueryParams.page_number;
    }

    if (!sendVertexParams) {
      delete apiQueryParams.anchor_type;
      delete apiQueryParams.anchor_context;
    }

    const queryString = Object.entries(apiQueryParams)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
      )
      .join("&");

    return queryString ? `?${queryString}` : "";
  },
);

export const getVertexQuery = createSelector(
  (queryParamsFilters: FiltersObject) => queryParamsFilters,
  (queryParamsFilters: FiltersObject) => {
    let queries;
    if (
      queryParamsFilters[VERTEX_TYPE] === "CLUSTER" &&
      !!queryParamsFilters[VERTEX_CONTEXT]
    ) {
      queries = queryParamsFilters[VERTEX_CONTEXT];
    } else if (queryParamsFilters[VERTEX_TYPE] === "UPLOAD") {
      const anchorParam = "anchor_type=UPLOAD";
      if (!queries) {
        queries = `?${anchorParam}`;
      } else {
        queries = `${queries}&${anchorParam}`;
      }
    } else {
      const renderParam = "render=MEDIA";
      if (!queries) {
        queries = `?${renderParam}`;
      } else {
        queries = `${queries}&${renderParam}`;
      }
    }
    if (queries && !queries.startsWith("?")) {
      queries = `?${queries}`;
    }
    return queries;
  },
);

// TODO: delete and use getExportRequestDataWithVqlFilters instead - VL-5589
export const getExportRequestData = createSelector(
  [
    (state: any, selectedFilters: any, temporary?: boolean) =>
      temporary
        ? getTemporaryExportDataArray(state)
        : getExportDataArray(state),
    getAllSelectedFilters,
  ],
  (exportDataArray, allSelectedFilters: any) => {
    let requestExportData = [];
    let mediaIDs: any[] = [];

    exportDataArray.forEach((singleItem) => {
      if (singleItem.exportType === "cluster") {
        let singleClusterData: any = {
          type: "cluster",
          payload: {
            cluster_id: singleItem.cluster_id,
            cluster_filters: singleItem.cluster_filters || allSelectedFilters,
          },
        };
        if (
          singleItem.excludeMediaIDs &&
          singleItem.excludeMediaIDs.length > 0
        ) {
          singleClusterData.payload.exclude_media_ids =
            singleItem.excludeMediaIDs;
        }
        requestExportData.push(singleClusterData);
      } else if (singleItem.exportType === "media") {
        mediaIDs.push(singleItem.media_id);
      }
    });

    if (mediaIDs.length > 0) {
      requestExportData.push({
        type: "media",
        payload: { media_ids: mediaIDs, cluster_filters: allSelectedFilters },
      });
    }

    return requestExportData;
  },
);

export const getAssignedTags = createSelector(
  state,
  (state): FilterOption[] => {
    return state.assignedTags;
  },
);

export const getTemporaryAssignedTags = createSelector(
  state,
  (state): FilterOption[] => {
    return state.temporaryAssignedTags;
  },
);

export const getSelectedCardTags = createSelector(
  [getSimilarityClusters, getTemporaryExportDataArray],
  (clusters, currentData) => {
    const selectedTags = new Set<string>();

    currentData.forEach(({ exportType, cluster_id, media_id }) => {
      const existingCluster = clusters?.find(
        (c: any) => c.cluster_id === cluster_id,
      );
      if (!existingCluster) return;

      if (exportType === ExportType.CLUSTER) {
        Object.keys(existingCluster.user_tags || {}).forEach((tag) =>
          selectedTags.add(tag),
        );
      }

      if (exportType === ExportType.MEDIA) {
        const preview = existingCluster.previews?.find(
          (p: { media_id: string }) => p.media_id === media_id,
        );
        preview?.user_tags?.forEach((tag: string) => selectedTags.add(tag));
      }
    });

    return selectedTags;
  },
);

export const getIsDataIngestionTabEnabled = createSelector(
  getUserConfig,
  (userConfig: any) => {
    return isFeatureEnabled(userConfig, Features.DATA_INGESTION_TAB_AVAILABLE);
  },
);

export const getNavigationClusterID = createSelector(state, (state: any) => {
  return state.navigationCluster?.cluster_id || "";
});

export const getShowExportModal = createSelector(state, (state: any) => {
  return state.showExportModal;
});

export const getIsShowDebugInfoEnabled = createSelector(
  getUserConfig,
  (userConfig: any): boolean => {
    return isFeatureEnabled(userConfig, Features.EXPLORATION_DEBUG_INFO);
  },
);

export const getExportProgress = createSelector(state, (state): number => {
  return state.exportProgress;
});

export const getExportStatus = createSelector(state, (state): string | null => {
  return state.exportStatus;
});

export const getUsersWithAccess = createSelector(state, (state: any) => {
  return state.usersWithAccess;
});

export const getInvitedEmails = createSelector(state, (state: any) => {
  return state.invitedEmails;
});

export const getLastSelectedIndex = createSelector(
  state,
  (state): string | null => {
    return state.lastSelectedIndex;
  },
);

export const getNoResultsFound = createSelector(state, (state): boolean => {
  return state.noResultsFound;
});

export const getMetadataCount = createSelector(
  getRawMetadataSummary,
  (rawMD: any): number | null => {
    return rawMD?.totalElements || null;
  },
);

export const getEnrichedModelsFromStats = createSelector(
  state,
  (state): EnrichmentModel[] => {
    const enrichedModels: EnrichmentModel[] = Object.values(
      state.stats?.dataset?.enrichment_models?.enrichment_models || {},
    );

    return enrichedModels;
  },
);

export const getIsPromptSupportOptions = createSelector(
  getUserConfig,
  (userConfig: any): { enabled: boolean; maxChars: number } => {
    let maxChars = 300; //Default

    const promptFeature = userConfig.find(
      (feature: any) => feature.feature_key === Features.PROMPT_SUPPORT_ENABLED,
    );

    if (promptFeature && promptFeature.featureOptions?.vqa_prompt_max_size) {
      maxChars = promptFeature.featureOptions.vqa_prompt_max_size;
    }

    const enabled = isFeatureEnabled(
      userConfig,
      Features.PROMPT_SUPPORT_ENABLED,
    );

    return { enabled, maxChars };
  },
);

// Defaults used until /user_config returns IMAGE_DISPLAY options
const DEFAULT_CENTER_CROP_SIZE = 256;
const DEFAULT_ROI_SQUARE_SIZE = 100;

export const getImageDisplayOptions = createSelector(
  getUserConfig,
  (userConfig: any): { centerCropSize: number; roiSquareSize: number } => {
    const options = userConfig?.find(
      (feature: any) => feature.feature_key === Features.IMAGE_DISPLAY,
    )?.featureOptions;

    return {
      centerCropSize: options?.center_crop_size ?? DEFAULT_CENTER_CROP_SIZE,
      roiSquareSize: options?.roi_square_size ?? DEFAULT_ROI_SQUARE_SIZE,
    };
  },
);

export const getIsSavedViewsLoading = createSelector(
  state,
  (state): boolean => {
    return state.savedViewsLoading;
  },
);

export const getShowDatasetSharingMenu = createSelector(
  state,
  (state: any): boolean => {
    return state.showDatasetSharingMenu;
  },
);

export const getShowViewSharingMenu = createSelector(
  state,
  (state: any): boolean => {
    return state.viewSharingMenu.showSharingMenu;
  },
);

export const getIsDuplicatesThresholdEnabled = createSelector(
  getUserConfig,
  (userConfig: any): boolean => {
    return isFeatureEnabled(userConfig, Features.CLUSTER_BY_DUPLICATES);
  },
);

export const getDuplicateThresholdRange = createSelector(
  state,
  (state): DuplicatesThresholdRange | null => {
    return state.duplicateThresholdRange;
  },
);

const getMetadataSourcesParam = (state: any, queryParams: FiltersObject) =>
  queryParams[METADATA_SOURCES_PARAMETER];

export const getEnrichmentModels = createSelector(
  state,
  (state): MetadataSource[] => state.enrichmentModels,
);

export const getExternalSources = createSelector(
  state,
  (state): MetadataSource[] => state.externalSources,
);

export const getIsMetadataSourcesLoading = createSelector(
  state,
  (state) => state.metadataSourcesLoading,
);

const isSourceSelected = (sources: any, singleSource: any) => {
  return !sources ? true : sources.includes(singleSource.id || "");
};

export const getDatasetEnrichmentModels = createSelector(
  [getEnrichmentModels, getMetadataSourcesParam],
  (enrichmentModels, metadataSourcesParam): MetadataSource[] => {
    const metadataSources = metadataSourcesParam
      ? JSON.parse(metadataSourcesParam)
      : null;

    return enrichmentModels.map((model: MetadataSource) => ({
      ...model,
      selected: isSourceSelected(metadataSources, model),
    }));
  },
);

export const getDatasetExternalSources = createSelector(
  [getExternalSources, getMetadataSourcesParam],
  (externalSources, metadataSourcesParam): MetadataSource[] => {
    const metadataSources = metadataSourcesParam
      ? JSON.parse(metadataSourcesParam)
      : null;

    return externalSources.map((source: MetadataSource) => ({
      ...source,
      selected: isSourceSelected(metadataSources, source),
    }));
  },
);

export const getIsUniquenessFilterEnabled = createSelector(
  getUserConfig,
  (userConfig: any): boolean => {
    return isFeatureEnabled(userConfig, Features.UNIQUENESS_SCORE);
  },
);

export const getIsMislabelesThresholdEnabled = createSelector(
  getUserConfig,
  getEntityTypeValue,
  (userConfig: any, entityType: EntityType | null): boolean => {
    const featureKeyMap: Record<EntityType, string> = {
      IMAGES: Features.MISLABEL_CONFIDENCE_FILTER_FOR_IMAGES,
      OBJECTS: Features.MISLABEL_CONFIDENCE_FILTER_FOR_OBJECTS,
    };

    return isFeatureEnabled(userConfig, featureKeyMap[entityType || "IMAGES"]);
  },
);

export const getMislabelsThresholdRange = createSelector(
  getUserConfig,
  getIsMislabelesThresholdEnabled,
  (state: any, entityType: EntityType | null) => entityType,
  (
    userConfig: any,
    isMislabelesThresholdEnabled: boolean,
    entityType: EntityType | null,
  ): [number, number] | null => {
    if (isMislabelesThresholdEnabled) {
      let threshold: number[] = [];
      const featureKeyMap: Record<EntityType, string> = {
        IMAGES: Features.MISLABEL_CONFIDENCE_FILTER_FOR_IMAGES,
        OBJECTS: Features.MISLABEL_CONFIDENCE_FILTER_FOR_OBJECTS,
      };
      const featureKey = featureKeyMap[entityType || "IMAGES"];

      userConfig?.forEach((feature: any) => {
        if (feature.feature_key === featureKey) {
          threshold = Object.values(feature.featureOptions || {});
        }
      });

      return threshold.length === 2 ? (threshold as [number, number]) : null;
    }
    return null;
  },
);

export const getIsOwner = createSelector(getUserConfig, (userConfig: any) => {
  return isFeatureEnabled(userConfig, Features.ENRICHMENT_ENRICH_DATASET);
});

export const getRemovedElementsByUniquenessCount = createSelector(
  state,
  (state): number | null => {
    return state.removedElementsByUniquenessCount;
  },
);

export const getUniquenessScoreLoading = createSelector(
  state,
  (state): boolean => {
    return state.uniquenessScoreLoading;
  },
);

export const getIsPublicDataset = createSelector(
  getDataset,
  (dataset): boolean => {
    return !!dataset?.sample || false;
  },
);

export const getRelevanceScores = createSelector(
  getUserConfig,
  getSearchOptions,
  (state: any, entityParam: EntityType | null) => entityParam,
  (
    userConfig: any,
    searchOptions: SearchFilterOptions,
    entityParam: EntityType | null,
  ): number[] => {
    if (searchOptions.semanticSearch) {
      let relevanceScores: number[] = [];

      const featureKeyMap: Record<EntityType, string> = {
        IMAGES: Features.TEXTUAL_SEARCH_RELEVANCE_SCORE_IMAGES,
        OBJECTS: Features.TEXTUAL_SEARCH_RELEVANCE_SCORE_OBJECTS,
      };

      userConfig?.forEach((feature: any) => {
        const isRelevantFeature =
          feature.feature_key === featureKeyMap[entityParam || "IMAGES"];

        if (isRelevantFeature) {
          relevanceScores =
            feature.featureOptions?.[
              "TEXTUAL_SEARCH_RELEVANCE_SCORE_THRESHOLDS"
            ] || [];
        }
      });

      return relevanceScores;
    }
    return [];
  },
);

export const getIsLabelPropagationEnabled = createSelector(
  getUserConfig,
  (config) => {
    return isFeatureEnabled(config, Features.FLYWHEEL);
  },
);

export const getIsExplorationPerformanceEnabled = createSelector(
  getUserConfig,
  (config) => {
    return isFeatureEnabled(config, Features.EXPLORATION_PERFORMANCE);
  },
);

const RANGE_ISSUES = ["OUTLIERS", "MISLABELS", "DUPLICATES", "LABEL_OUTLIER"];

export const getFilteredIssues = createSelector(
  getDatasetIssueTypes,
  (issues) => {
    return issues.filter(
      (issue) => !RANGE_ISSUES.includes(issue.text.toUpperCase()),
    );
  },
);

export const getFiltersMenuOptions = createSelector(
  [
    getIssueTypes,
    getIsUniquenessFilterEnabled,
    getIsDuplicatesThresholdEnabled,
    getIsAddMediaEnabled,
    (state, filters: FilterMenuItem[], entityType: EntityType | null) => ({
      filters,
      entityType,
    }),
  ],
  (
    issues,
    isUniqenessEnabled,
    isDuplicatesThresholdEnabled,
    isAddMediaEnabled,
    { filters, entityType },
  ) => {
    const availableIssues = new Set(
      issues.map((i) => i.text.toUpperCase().replace(/\s+/g, "_")),
    );

    const hasNonRangeIssues = issues.some(
      (i) => !RANGE_ISSUES.includes(i.text.toUpperCase()),
    );

    // Filter static filters
    const staticFilters = filters
      .filter((filter) => {
        if (
          filter.entityTypes &&
          entityType &&
          !filter.entityTypes.includes(entityType)
        ) {
          return false;
        }

        if (filter.type === Filters.ISSUES) return hasNonRangeIssues;
        if (filter.type === Filters.UNIQUENESS) return isUniqenessEnabled;
        if (filter.type === Filters.CREATED_AT) return isAddMediaEnabled;
        if (!RANGE_ISSUES.includes(filter.type)) return true;
        return availableIssues.has(filter.type);
      })
      .map((filter) => {
        if (
          filter.type === Filters.DUPLICATES &&
          !isDuplicatesThresholdEnabled
        ) {
          return { ...filter, disabled: true };
        }
        return filter;
      });

    // Combine static filters with dynamic custom metadata filters
    return [...staticFilters];
  },
);

export const getShowQueryPanel = (
  state: State,
  vqlQueries: VqlQuery[],
): boolean => {
  return vqlQueries.length > 0 && state.singleDataset.showQueryPanel;
};

export const getLabelPropagationFeatureOptions = createSelector(
  getUserConfig,
  (config) => {
    return (
      config.find((config: any) => config.feature_key === "FLYWHEEL")
        ?.featureOptions || {}
    );
  },
);

export const getGlobalMDType = (state: State): MetadataType =>
  (state as any).singleDataset.globalMDType;

export const getIsTrainWorthy = createSelector(
  getUserConfig,
  (userConfig: any) => {
    return isFeatureEnabled(userConfig, Features.TRAIN_WORTHY);
  },
);

export const getIsDatasetSnapshotEnabled = createSelector(
  getUserConfig,
  (userConfig: any) => {
    return isFeatureEnabled(userConfig, Features.DATASET_SNAPSHOTS);
  },
);

export const getIsTrainModelEnabled = createSelector(
  getUserConfig,
  (userConfig) => {
    return isFeatureEnabled(userConfig, TRAIN_MODEL);
  },
);

export const getIsUserManagementEnabled = createSelector(
  getUserConfig,
  (userConfig) => {
    return isFeatureEnabled(userConfig, "USER_MANAGEMENT");
  },
);
